import { Link } from 'react-router-dom'
import styled, { css, keyframes } from 'styled-components'
import { Surface } from '../../components/DashboardPrimitives'
import { WINDOW_SIZE } from '../../util/Constants'

// Design language for the Money home: banking-app hierarchy on the shared
// dashboard system. 8px spacing grid, background contrast instead of hard
// borders, tabular numerals on every amount, focus-visible rings on every
// interactive element, and one subtle reveal transition on expansion.

export const focusRing = css`
  &:focus-visible {
    box-shadow: 0 0 0 3px rgba(49, 87, 198, 0.28);
    outline: none;
  }
`

const revealDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-2px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

export const MoneyWorkspace = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin: 8px auto 0;
  max-width: 1080px;
  min-width: 0;
  width: 100%;
`

export const MoneyLayout = styled.div`
  align-items: start;
  display: grid;
  gap: 20px;
  grid-template-columns: minmax(0, 1fr) 320px;
  min-width: 0;
  width: 100%;

  @media (max-width: ${WINDOW_SIZE.LAPTOP}) {
    grid-template-columns: 1fr;
  }
`

export const MoneyColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
`

export const SideColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`

// ---------------------------------------------------------------------------
// Hero: total balance and the three money actions
// ---------------------------------------------------------------------------

export const HeroRow = styled.div`
  align-items: flex-end;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  min-width: 0;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    align-items: flex-start;
    flex-direction: column;
  }
`

export const HeroBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`

export const EyebrowRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`

export const Eyebrow = styled.span`
  color: var(--text-muted, #6f7888);
  font-size: 11.5px;
  font-weight: 500;
  letter-spacing: 0.06em;
  line-height: 1.25;
  text-transform: uppercase;
`

export const HeroTotal = styled.div`
  color: var(--ink-strong, #121722);
  font-size: 40px;
  font-variant-numeric: tabular-nums;
  font-weight: 550;
  letter-spacing: -0.015em;
  line-height: 1.1;
  overflow-wrap: anywhere;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    font-size: 32px;
  }
`

export const HeroMeta = styled.span`
  color: var(--text-soft, #7d8492);
  font-size: 12.5px;
  line-height: 1.4;
`

export const HeroActions = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

// ---------------------------------------------------------------------------
// Account groups and rows
// ---------------------------------------------------------------------------

export const GroupStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`

export const GroupLabel = styled.span`
  color: var(--text-muted, #6f7888);
  font-size: 11.5px;
  font-weight: 500;
  letter-spacing: 0.05em;
  line-height: 1.25;
  padding: 0 4px;
  text-transform: uppercase;
`

export const GroupSurface = styled(Surface)`
  gap: 2px;
  padding: 8px;
`

const rowShell = css`
  align-items: center;
  border-radius: 12px;
  display: grid;
  gap: 12px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  min-width: 0;
  padding: 10px 12px;
  width: 100%;
`

export const RowChevron = styled.span<{ $open?: boolean }>`
  align-items: center;
  color: var(--text-faint, #9aa3b2);
  display: inline-flex;
  transform: rotate(${({ $open }) => $open ? '180deg' : '0deg'});
  transition: transform 0.22s cubic-bezier(0.22, 1, 0.36, 1), color 0.16s ease;
`

export const AccountRowButton = styled.button`
  ${rowShell}
  background: transparent;
  border: 0;
  cursor: pointer;
  font-family: var(--font-sans);
  text-align: left;
  transition: background-color 0.14s ease;

  &:hover {
    background: var(--surface-hover, #f5f7fb);

    ${RowChevron} {
      color: var(--text-muted, #5f6675);
    }
  }

  &:active {
    background: var(--surface-pressed, #eef1f7);
  }

  ${focusRing}
`

export const AccountRowStatic = styled.div`
  ${rowShell}
`

export const RowIconCircle = styled.div<{ $muted?: boolean }>`
  align-items: center;
  background: var(--surface-pressed, #eef2f7);
  border-radius: 999px;
  color: ${({ $muted }) => $muted ? 'var(--text-faint, #9aa3b2)' : 'var(--text-muted, #5f6675)'};
  display: flex;
  flex: 0 0 auto;
  height: 36px;
  justify-content: center;
  width: 36px;
`

export const AssetLogoCircle = styled.div<{ $logo?: string }>`
  background-image: ${({ $logo }) => $logo ? `url(${$logo})` : 'none'};
  background-color: ${({ $logo }) => $logo ? 'transparent' : 'var(--surface-pressed, #eef2f7)'};
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  border-radius: 999px;
  flex: 0 0 auto;
  height: 34px;
  margin: 1px;
  width: 34px;
`

export const RowMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`

export const RowTitleLine = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  min-width: 0;
`

export const RowTitle = styled.span`
  color: var(--ink, #20242d);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const RowSub = styled.span`
  color: var(--text-soft, #7d8492);
  font-size: 12.5px;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const RowEnd = styled.div`
  align-items: flex-end;
  display: flex;
  flex-direction: column;
  gap: 3px;
  text-align: right;
`

export const RowEndInline = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
`

export const RowAmount = styled.span`
  color: var(--ink-strong, #121722);
  font-size: 14px;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
  line-height: 1.25;
  white-space: nowrap;
`

export const RowAmountSub = styled.span`
  color: var(--text-faint, #8a919e);
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  line-height: 1.3;
  white-space: nowrap;
`

// ---------------------------------------------------------------------------
// Inline expansion panels (bank details, per-network breakdown)
// ---------------------------------------------------------------------------

export const ExpandPanel = styled.div`
  animation: ${revealDown} 0.2s cubic-bezier(0.22, 1, 0.36, 1);
  background: var(--surface-muted, #f8fafc);
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  margin: 0 4px 8px 58px;
  min-width: 0;
  padding: 10px 16px 12px;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    margin-left: 4px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

export const DetailRow = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  min-height: 26px;
  padding: 7px 0;
`

export const DetailLabel = styled.span`
  color: var(--text-muted, #6b7280);
  flex: 0 0 auto;
  font-size: 12.5px;
  line-height: 1.35;
`

export const DetailValue = styled.span<{ $mono?: boolean }>`
  color: var(--ink, #20242d);
  font-family: ${({ $mono }) => $mono ? 'var(--font-mono)' : 'var(--font-sans)'};
  font-size: 12.75px;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
  line-height: 1.35;
  overflow-wrap: anywhere;
  text-align: right;
`

export const DetailValueGroup = styled.div`
  align-items: center;
  display: flex;
  gap: 6px;
  justify-content: flex-end;
  min-width: 0;
`

export const PanelNote = styled.span`
  color: var(--text-soft, #7d8492);
  font-size: 12px;
  line-height: 1.45;
  padding: 7px 0 6px;
`

export const PanelHeading = styled.span`
  color: var(--ink, #20242d);
  font-size: 13px;
  font-weight: 500;
  line-height: 1.3;
  padding: 10px 0 2px;
`

/** Small uppercase divider inside an expansion panel (e.g. recent activity). */
export const PanelSectionLabel = styled.span`
  color: var(--text-muted, #6f7888);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.05em;
  line-height: 1.3;
  padding: 10px 0 2px;
  text-transform: uppercase;
`

/** One pooled-rail action: ink-underline text action over a one-line explainer. */
export const RailAction = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 7px 0;
`

export const ActionExplainer = styled.span`
  color: var(--text-soft, #7d8492);
  font-size: 12px;
  line-height: 1.4;
`

export const NetworkRow = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  min-height: 34px;
  padding: 6px 0;
`

export const NetworkName = styled.span`
  color: var(--ink, #20242d);
  font-size: 13px;
  font-weight: 500;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const NetworkAmount = styled.span`
  color: var(--ink, #20242d);
  font-size: 12.75px;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
  line-height: 1.3;
  white-space: nowrap;
`

/** Muted right-side line for accounts with no queryable balance. */
export const RowMutedText = styled.span`
  color: var(--text-faint, #8a919e);
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1.3;
  white-space: nowrap;
`

// ---------------------------------------------------------------------------
// Asset drill-down: action row and recent activity list
// ---------------------------------------------------------------------------

export const AssetActionRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 8px 0 4px;
`

export const ActionDivider = styled.span`
  color: var(--border-strong, #c2c9d6);
  font-size: 12px;
  line-height: 1;
  user-select: none;
`

export const ActivityRow = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  min-height: 34px;
  padding: 5px 0;
`

export const ActivityMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

export const ActivityLabel = styled.span`
  color: var(--ink, #20242d);
  font-size: 12.75px;
  font-weight: 500;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const ActivityTime = styled.span`
  color: var(--text-faint, #8a919e);
  font-size: 11.5px;
  line-height: 1.3;
  white-space: nowrap;
`

export const ActivityAmount = styled.span<{ $direction: 'in' | 'out' }>`
  color: ${({ $direction }) => $direction === 'in' ? '#16724b' : 'var(--ink, #20242d)'};
  flex: 0 0 auto;
  font-size: 12.75px;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
  line-height: 1.3;
  white-space: nowrap;
`

// ---------------------------------------------------------------------------
// Text buttons
// ---------------------------------------------------------------------------

export const InlineTextButton = styled.button`
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: var(--ink, #20242d);
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 12.5px;
  font-weight: 500;
  text-decoration: underline;
  text-underline-offset: 3px;
  line-height: 1.35;
  padding: 0 2px;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }

  ${focusRing}
`

/** Router-link twin of InlineTextButton: ink text with the brand underline. */
export const InlineTextLink = styled(Link)`
  border-radius: 6px;
  color: var(--ink, #20242d);
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1.35;
  padding: 0 2px;
  text-decoration: underline;
  text-underline-offset: 3px;
  white-space: nowrap;
  width: fit-content;

  &:hover {
    text-decoration: underline;
  }

  ${focusRing}
`

export const HistoryTextButton = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 8px;
  color: var(--text-muted, #6c7483);
  cursor: pointer;
  display: inline-flex;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  gap: 6px;
  line-height: 1.2;
  padding: 4px 6px;
  transition: color 0.16s ease;
  width: fit-content;

  &:hover {
    color: var(--ink, #20242d);
  }

  ${focusRing}
`

export const MutedNote = styled.span`
  color: var(--text-soft, #7d8492);
  font-size: 12.5px;
  line-height: 1.45;
  padding: 4px;
`

// ---------------------------------------------------------------------------
// Sidebar: pulse card and activity feed
// ---------------------------------------------------------------------------

export const PulseGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
`

export const PulseStat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

export const PulseLabel = styled.span`
  color: var(--text-muted, #6f7888);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.04em;
  line-height: 1.3;
  text-transform: uppercase;
`

export const PulseValue = styled.span`
  color: var(--ink-strong, #121722);
  font-size: 19px;
  font-variant-numeric: tabular-nums;
  font-weight: 550;
  line-height: 1.15;
  overflow-wrap: anywhere;
`

export const FeedList = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

export const FeedLink = styled(Link)`
  align-items: center;
  border-radius: 10px;
  color: inherit;
  display: grid;
  gap: 10px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  padding: 8px;
  text-decoration: none;
  transition: background-color 0.16s ease;

  &:hover {
    background: var(--surface-hover, #f5f7fb);
  }

  ${focusRing}
`

export const FeedBadge = styled.div<{ $direction: 'in' | 'out' }>`
  align-items: center;
  background: ${({ $direction }) => $direction === 'in' ? '#e8f7ef' : '#eef2f7'};
  border-radius: 999px;
  color: ${({ $direction }) => $direction === 'in' ? '#16724b' : '#5f6675'};
  display: flex;
  flex: 0 0 auto;
  height: 28px;
  justify-content: center;
  width: 28px;
`

export const FeedTitle = styled.span`
  color: var(--ink, #20242d);
  font-size: 13px;
  font-weight: 500;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const FeedSub = styled.span`
  color: var(--text-soft, #7d8492);
  font-size: 11.5px;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const FeedAmount = styled.span<{ $direction: 'in' | 'out' }>`
  color: ${({ $direction }) => $direction === 'in' ? '#16724b' : 'var(--ink, #20242d)'};
  font-size: 12.75px;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
  line-height: 1.3;
  white-space: nowrap;
`

// ---------------------------------------------------------------------------
// Drill-down panel actions: real buttons, not link soup. Merchants should see
// one obvious action set per asset.
// ---------------------------------------------------------------------------
export const PanelActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 4px 0 8px;
`

export const PanelPillButton = styled.button<{ $primary?: boolean }>`
  align-items: center;
  background: ${({ $primary }) => $primary ? 'var(--primary-btn-bg, #17191f)' : 'var(--surface, #ffffff)'};
  border: 1px solid ${({ $primary }) => $primary ? 'var(--primary-btn-bg, #17191f)' : 'var(--border-strong, #dfe4ec)'};
  border-radius: 999px;
  color: ${({ $primary }) => $primary ? 'var(--primary-btn-ink, #ffffff)' : 'var(--ink, #20242d)'};
  cursor: pointer;
  display: inline-flex;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 550;
  gap: 8px;
  line-height: 1;
  min-height: 36px;
  padding: 0 16px;
  transition:
    background-color 0.16s ease,
    border-color 0.16s ease,
    box-shadow 0.16s ease,
    transform 0.12s ease;

  &:hover {
    background: ${({ $primary }) => $primary ? 'var(--primary-btn-bg-hover, #000000)' : 'var(--surface-hover, #f6f7f9)'};
    border-color: ${({ $primary }) => $primary ? 'var(--primary-btn-bg-hover, #000000)' : 'var(--border-strong, #c9d0dc)'};
    box-shadow: 0 3px 10px rgba(15, 23, 42, 0.09);
    transform: translateY(-1px);
  }

  &:active {
    box-shadow: none;
    transform: translateY(0) scale(0.99);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;

    &:hover {
      background: ${({ $primary }) => $primary ? 'var(--primary-btn-bg, #17191f)' : 'var(--surface, #ffffff)'};
      border-color: ${({ $primary }) => $primary ? 'var(--primary-btn-bg, #17191f)' : 'var(--border-strong, #dfe4ec)'};
      box-shadow: none;
      transform: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    &:hover, &:active {
      transform: none;
    }
  }

  ${focusRing}
`

export const PanelPillGlyph = styled.span`
  color: currentColor;
  font-size: 12.5px;
  line-height: 1;
  opacity: 0.72;
`

export const GhostViewAllButton = styled(Link)`
  align-items: center;
  border: 0;
  border-radius: 8px;
  color: var(--text-muted, #6b7280);
  display: inline-flex;
  font-size: 12px;
  font-weight: 500;
  padding: 6px 2px;
  text-decoration: underline;
  text-underline-offset: 3px;

  &:hover {
    color: var(--ink, #20242d);
  }

  ${focusRing}
`

// ---------------------------------------------------------------------------
// Currency rails: EUR/USD/GBP rows with per-capability availability inside.
// ---------------------------------------------------------------------------

export const CurrencySymbolGlyph = styled.span`
  color: var(--text-muted, #5f6675);
  font-size: 15px;
  font-weight: 600;
  line-height: 1;
`

export const RailOptionRow = styled.div<{ $muted?: boolean }>`
  align-items: center;
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(0, 1fr) auto;
  min-height: 44px;
  padding: 7px 2px;

  & + & {
    border-top: 1px solid #eef1f7;
  }

  opacity: ${({ $muted }) => $muted ? 0.75 : 1};
`

export const RailOptionMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

export const RailOptionTitle = styled.span`
  color: var(--ink, #20242d);
  font-size: 13px;
  font-weight: 500;
  line-height: 1.3;
`

export const RailOptionDesc = styled.span`
  color: var(--text-faint, #8a919e);
  font-size: 12px;
  line-height: 1.35;
`

// Inline reporting-currency trigger inside the hero meta line.
export const CurrencySwitchTrigger = styled.span`
  align-items: center;
  border-bottom: 1px dashed var(--border-strong, #c9d0dc);
  color: var(--ink, #20242d);
  cursor: pointer;
  display: inline-flex;
  font-weight: 550;
  gap: 4px;
  line-height: 1.2;
  transition: color 0.16s ease, border-color 0.16s ease;

  &:hover {
    border-bottom-color: var(--ink, #20242d);
  }
`
